/**
 * Motion system for datasyte.ai.
 *
 * Motion exists to clarify relationships and workflow: how a signal travels,
 * how a timeline advances, how one interface state becomes the next. It is
 * never decorative spectacle. Every token below is deliberately short and
 * low-amplitude so the site reads as clinical software, not a video game.
 *
 * Rules encoded here:
 *  - `prefers-reduced-motion: reduce` collapses every duration to 0 and every
 *    offset to 0, leaving a static, fully legible composition.
 *  - Nothing in this module touches `window` or `document` at module scope, so
 *    it is safe to import from code that may be rendered on a server.
 *  - Amplitudes are capped: no travel over 24px, no scale change over 4%.
 */

import { useEffect, useState } from "react";

/* ------------------------------------------------------------------ tokens */

/** Durations in seconds. Nothing on the site animates longer than `slowest`. */
export const duration = {
  /** Hover and focus feedback. */
  instant: 0.12,
  /** State swaps inside a panel. */
  fast: 0.2,
  /** Default for reveals and tab changes. */
  base: 0.32,
  /** Timeline steps and path traversal. */
  slow: 0.5,
  /** Full-width diagram traversal only. */
  slowest: 0.8,
} as const;

/** Easing curves. `standard` is the default for almost everything. */
export const easing = {
  /** Calm, decelerating — entrances and state changes. */
  standard: [0.22, 0.61, 0.36, 1] as const,
  /** Symmetric — position changes that stay on screen. */
  inOut: [0.4, 0, 0.2, 1] as const,
  /** Exits and dismissals. */
  exit: [0.4, 0, 1, 1] as const,
  /** Continuous loops (pulses, path flow) so there is no visible seam. */
  loop: "linear" as const,
};

/** Travel distances in px. Reveals move a short distance, never a swoop. */
export const distance = {
  /** Interface-state swap. */
  sm: 6,
  /** Default reveal offset. */
  md: 12,
  /** Section-level reveal offset (maximum). */
  lg: 20,
} as const;

/** Stagger between siblings in a group reveal, in seconds. */
export const stagger = {
  tight: 0.04,
  base: 0.07,
  loose: 0.1,
} as const;

/** Loop periods for ambient signals, in seconds. Slow enough to read as calm. */
export const loopPeriod = {
  /** Data pulse on a node. */
  pulse: 2.4,
  /** Light travelling along a connection path. */
  path: 3.2,
  /** Ambient status breathing. */
  ambient: 4,
} as const;

/* -------------------------------------------------- reduced-motion plumbing */

const QUERY = "(prefers-reduced-motion: reduce)";

/**
 * SSR-safe `prefers-reduced-motion` subscription.
 *
 * Starts as `false` (motion allowed) and never reads `window` during render,
 * so server rendering and hydration agree. The first effect pass corrects the
 * value before any animation is perceivable.
 */
export const useReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(QUERY);
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return reduced;
};

/** One-shot read for imperative code (observers, video autoplay decisions). */
export const prefersReducedMotion = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(QUERY).matches;

/* ------------------------------------------------------- variant factories */

type Axis = "up" | "down" | "left" | "right" | "none";

const offsetFor = (axis: Axis, px: number) => {
  switch (axis) {
    case "up":
      return { y: px };
    case "down":
      return { y: -px };
    case "left":
      return { x: px };
    case "right":
      return { x: -px };
    default:
      return {};
  }
};

/**
 * Reveal variants for a single element. When `reduced` is true the element is
 * simply present — no offset, no duration — which is the required static
 * fallback rather than a degraded animation.
 */
export const revealVariants = (
  reduced: boolean,
  {
    axis = "up",
    px = distance.md,
    delay = 0,
    seconds = duration.base,
  }: { axis?: Axis; px?: number; delay?: number; seconds?: number } = {},
) => ({
  hidden: reduced ? { opacity: 1 } : { opacity: 0, ...offsetFor(axis, px) },
  visible: {
    opacity: 1,
    x: 0,
    y: 0,
    transition: reduced
      ? { duration: 0 }
      : { duration: seconds, delay, ease: easing.standard },
  },
});

/** Parent variants that stagger children through their own `visible` state. */
export const staggerVariants = (
  reduced: boolean,
  { step = stagger.base, delay = 0 }: { step?: number; delay?: number } = {},
) => ({
  hidden: {},
  visible: {
    transition: reduced
      ? { staggerChildren: 0, delayChildren: 0 }
      : { staggerChildren: step, delayChildren: delay },
  },
});

/** Interface-state swap: a short cross-dissolve with a 6px settle. */
export const stateSwapVariants = (reduced: boolean) => ({
  enter: reduced ? { opacity: 1, y: 0 } : { opacity: 0, y: distance.sm },
  active: {
    opacity: 1,
    y: 0,
    transition: reduced ? { duration: 0 } : { duration: duration.base, ease: easing.standard },
  },
  leave: reduced
    ? { opacity: 1 }
    : { opacity: 0, y: -distance.sm, transition: { duration: duration.fast, ease: easing.exit } },
});

/** Restrained hover feedback for cards and tiles. */
export const hoverLift = (reduced: boolean) =>
  reduced
    ? {}
    : {
        whileHover: { y: -2 },
        whileTap: { y: 0 },
        transition: { duration: duration.instant, ease: easing.standard },
      };

/* --------------------------------------------------- viewport reveal helper */

/** Shared `whileInView` config: fires once, slightly before the edge. */
export const inViewOnce = { once: true, margin: "-64px 0px -64px 0px" } as const;
